import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  type AdminChatUser,
  sortAdminChatUsers,
} from './adminChatUsersHelpers';
import { supabase } from '../services/supabase';

export type { AdminChatUser } from './adminChatUsersHelpers';

export interface AdminChatSummaryRow {
  other_user_id?: string | null;
  unread_count?: number | null;
  last_message?: string | null;
  last_message_time?: string | null;
}

const fetchAdminChatUsers = async (
  currentUserId: string,
  includeUserIds: string[] = []
): Promise<AdminChatUser[]> => {
  const [usersResult, summariesResult] = await Promise.all([
    supabase
      .from('users')
      .select('id, full_name, avatar, email')
      .order('created_at', { ascending: false })
      .limit(200),
    supabase.rpc('get_user_chat_summaries', {
      p_owner_user_id: currentUserId,
      p_other_user_ids: null,
    }),
  ]);

  const { data: usersData, error: usersError } = usersResult;
  if (usersError) throw usersError;

  const { data: summaryRows, error: summaryError } = summariesResult;
  if (summaryError) throw summaryError;

  const baseUsers = usersData ?? [];
  const normalizedSummaryRows = ((summaryRows ?? []) as AdminChatSummaryRow[]).filter(
    (row): row is Required<Pick<AdminChatSummaryRow, 'other_user_id'>> & AdminChatSummaryRow =>
      typeof row.other_user_id === 'string' && row.other_user_id.length > 0
  );
  const summaryUserIds = normalizedSummaryRows.map((row) => row.other_user_id);
  const includeIds = Array.from(
    new Set([...includeUserIds.filter(Boolean), ...summaryUserIds])
  ).filter((id) => id !== currentUserId);
  const baseIds = new Set(baseUsers.map((user) => user.id));
  const missingIds = includeIds.filter((id) => !baseIds.has(id));

  const extraUsers =
    missingIds.length > 0
      ? (
          await supabase
            .from('users')
            .select('id, full_name, avatar, email')
            .in('id', missingIds)
        ).data ?? []
      : [];

  const allUsers = [...baseUsers, ...extraUsers].filter((user) => user.id !== currentUserId);
  const summaryByUserId = new Map(
    normalizedSummaryRows.map((row) => [row.other_user_id, row])
  );

  const enriched = allUsers.map((user) => {
    const summary = summaryByUserId.get(user.id);

    return {
      id: user.id,
      fullName: user.full_name,
      avatar: user.avatar ?? undefined,
      email: user.email ?? null,
      unreadCount: summary?.unread_count ?? 0,
      lastMessage: summary?.last_message ?? undefined,
      lastMessageTime: summary?.last_message_time ?? undefined,
    };
  });

  return sortAdminChatUsers(enriched);
};

export const useAdminChatUsers = (currentUserId: string, includeUserIds: string[] = []) => {
  const queryClient = useQueryClient();
  const debounceRef = useRef<number | null>(null);
  const includeKey = includeUserIds.slice().sort().join(',');

  const query = useQuery({
    queryKey: ['admin-chat-users', currentUserId, includeKey],
    queryFn: () => fetchAdminChatUsers(currentUserId, includeUserIds),
    enabled: !!currentUserId,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!currentUserId) return;
    const includeSet = includeUserIds.length > 0 ? new Set(includeUserIds) : null;
    const scheduleInvalidate = () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['admin-chat-users', currentUserId],
          exact: false,
        });
      }, 300);
    };

    const channel = supabase
      .channel(`admin-chat-users-${currentUserId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const message = payload.new as { sender_id: string; receiver_id: string };
          const otherUserId =
            message.sender_id === currentUserId ? message.receiver_id : message.sender_id;
          if (message.sender_id === currentUserId || message.receiver_id === currentUserId) {
            if (!includeSet || includeSet.has(otherUserId)) {
              scheduleInvalidate();
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        (payload) => {
          const message = payload.new as { sender_id: string; receiver_id: string };
          const otherUserId =
            message.sender_id === currentUserId ? message.receiver_id : message.sender_id;
          if (message.sender_id === currentUserId || message.receiver_id === currentUserId) {
            if (!includeSet || includeSet.has(otherUserId)) {
              scheduleInvalidate();
            }
          }
        }
      )
      .subscribe();

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      void supabase.removeChannel(channel);
    };
  }, [currentUserId, includeUserIds, queryClient]);

  return query;
};
